import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Clock, User, Phone, Calendar, Timer, Users, Filter, HeartPulse, ChevronDown } from 'lucide-react';
import { LoadingButton } from './shared/LoadingComponents';
import { BUTTON_STYLES, CARD_STYLES } from '../utils/constants';
import { format } from 'date-fns';
import { formatISTDateTimeShort, formatISTDateShort } from '../utils/date';
import { useToast } from '../../../hooks/useToast';
import { appointmentAPI } from '../../../api/appointment';
import { useSocketContext } from '../../../context/SocketContext';

const INITIAL_VITALS_FORM = {
  heightCm: '',
  weightKg: '',
  bmi: '',
  bpSys: '',
  bpDia: '',
  pulse: '',
  temperature: '',
  oxygenSaturation: '',
  respiration: ''
};

// Vitals validation constants (matching backend schema)
const VITALS_VALIDATION = {
  heightCm: { min: 0, max: 300, label: 'Height' },
  weightKg: { min: 0, max: 1000, label: 'Weight' },
  bmi: { min: 0, max: 100, label: 'BMI' },
  bpSys: { min: 0, max: 300, label: 'BP Systolic' },
  bpDia: { min: 0, max: 200, label: 'BP Diastolic' },
  pulse: { min: 0, max: 300, label: 'Pulse' },
  temperature: { min: 0, max: 110, label: 'Temperature' },
  oxygenSaturation: { min: 0, max: 100, label: 'Oxygen Saturation' },
  respiration: { min: 0, max: 100, label: 'Respiration' },
  sugar: { min: 0, max: 1000, label: 'Sugar' },
  pft: { min: 0, max: 200, label: 'PFT' }
};

const CheckInOut = ({
  loading,
  appointments = [],
  appointmentsByDoctor = {},
  onCompleteAppointment = () => { },
  onCancelAppointment = () => { },
  onStartAppointment = () => { },
  onJoinQueue = () => { },
  onRefreshAppointments = () => { },
}) => {
  const { showSuccess, showError, showWarning, showInfo } = useToast();
  const { addEventListener, removeEventListener, isConnected } = useSocketContext();
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [checkInNotes, setCheckInNotes] = useState('');
  const [patientSearch, setPatientSearch] = useState('');
  const [activeQueueTab, setActiveQueueTab] = useState('all'); // 'all', 'waiting', 'in-progress'
  const [filterDoctor, setFilterDoctor] = useState(''); // doctor filter id
  const [filterDate, setFilterDate] = useState(format(new Date(), 'yyyy-MM-dd')); // date filter
  const [vitalsModal, setVitalsModal] = useState({ open: false, appointment: null });
  const [vitalsForm, setVitalsForm] = useState(() => ({ ...INITIAL_VITALS_FORM }));
  const [vitalsSubmitting, setVitalsSubmitting] = useState(false);
  const [vitalsErrors, setVitalsErrors] = useState({});
  const didInitialDateRefresh = useRef(false);

  const refreshAppointmentsForDate = useCallback(async (dateValue, options = {}) => {
    if (typeof onRefreshAppointments !== 'function' || !dateValue) {
      return;
    }
    try {
      await onRefreshAppointments(dateValue, dateValue, options);
    } catch (error) {
      console.error('Failed to refresh appointments for date filter:', error);
      if (!options?.silent) {
        showError('Unable to refresh appointments for the selected date.');
      }
    }
  }, [onRefreshAppointments, showError]);

  const handleDateFilterChange = useCallback((nextDate) => {
    setFilterDate(nextDate);
    refreshAppointmentsForDate(nextDate, { silent: true });
  }, [refreshAppointmentsForDate]);

  useEffect(() => {
    if (didInitialDateRefresh.current) return;
    didInitialDateRefresh.current = true;
    refreshAppointmentsForDate(filterDate, { silent: true });
  }, [filterDate, refreshAppointmentsForDate]);

  // Socket event handlers for real-time updates
  useEffect(() => {
    if (!isConnected || !addEventListener || !removeEventListener) {
      return;
    }

    // Handler for new appointment bookings
    const handleAppointmentBooked = (data) => {
      console.log('🔔 New appointment booked received in CheckInOut:', data);
      showInfo(`New appointment scheduled: ${data.appointment?.patientName || 'Unknown Patient'}`);

      // Refresh appointments to show the new appointment immediately
      if (onRefreshAppointments) {
        setTimeout(() => {
          onRefreshAppointments();
        }, 500); // Small delay to ensure backend has processed the appointment
      }
    };

    // Handler for patient joining queue
    const handlePatientAddedToQueue = (data) => {
      console.log('🔔 Patient added to queue received in CheckInOut:', data);
      showInfo(`${data.appointment?.patientName || 'Patient'} joined the queue`);

      // Refresh appointments to update queue status
      if (onRefreshAppointments) {
        setTimeout(() => {
          onRefreshAppointments();
        }, 500);
      }
    };

    // Handler for service started
    const handleServiceStarted = (data) => {
      console.log('🔔 Service started received in CheckInOut:', data);
      showInfo(`Consultation started for ${data.appointment?.patientName || 'Patient'}`);

      // Refresh appointments to update status
      if (onRefreshAppointments) {
        setTimeout(() => {
          onRefreshAppointments();
        }, 500);
      }
    };

    // Handler for service completed
    const handleServiceCompleted = (data) => {
      console.log('🔔 Service completed received in CheckInOut:', data);
      showSuccess(`Consultation completed for ${data.appointment?.patientName || 'Patient'}`);

      // Refresh appointments to update status
      if (onRefreshAppointments) {
        setTimeout(() => {
          onRefreshAppointments();
        }, 500);
      }
    };

    // Handler for queue updates
    const handleQueueUpdated = (data) => {
      console.log('🔔 Queue updated received in CheckInOut:', data);

      // Refresh appointments to update queue status
      if (onRefreshAppointments) {
        setTimeout(() => {
          onRefreshAppointments();
        }, 300);
      }
    };

    // Register event listeners
    addEventListener('appointment:booked', handleAppointmentBooked);
    addEventListener('queue:patient-added', handlePatientAddedToQueue);
    addEventListener('queue:service-started', handleServiceStarted);
    addEventListener('queue:service-completed', handleServiceCompleted);
    addEventListener('queue:updated', handleQueueUpdated);

    // Cleanup on unmount
    return () => {
      removeEventListener('appointment:booked', handleAppointmentBooked);
      removeEventListener('queue:patient-added', handlePatientAddedToQueue);
      removeEventListener('queue:service-started', handleServiceStarted);
      removeEventListener('queue:service-completed', handleServiceCompleted);
      removeEventListener('queue:updated', handleQueueUpdated);
    };
  }, [isConnected, addEventListener, removeEventListener, onRefreshAppointments, showInfo, showSuccess]);

  const vitalFieldMeta = [
    { key: 'heightCm', label: 'Height (cm)', step: '0.1', placeholder: 'e.g., 170' },
    { key: 'weightKg', label: 'Weight (kg)', step: '0.1', placeholder: 'e.g., 65' },
    { key: 'bmi', label: 'BMI', step: '0.1', placeholder: 'Auto-calculated', readOnly: true },
    { key: 'bpSys', label: 'BP Systolic', step: '1', placeholder: 'e.g., 120' },
    { key: 'bpDia', label: 'BP Diastolic', step: '1', placeholder: 'e.g., 80' },
    { key: 'pulse', label: 'Pulse (bpm)', step: '1', placeholder: 'e.g., 72' },
    { key: 'temperature', label: 'Temperature (°F)', step: '0.1', placeholder: 'e.g., 98.6' },
    { key: 'oxygenSaturation', label: 'SpO₂ (%)', step: '1', placeholder: 'e.g., 98' },
    { key: 'respiration', label: 'Respiration (rpm)', step: '1', placeholder: 'e.g., 16' }
  ];

  // Helper to extract comparable timestamp from appointment
  const getApptTimestamp = (app) => {
    if (app?.dateTime) {
      const t = Date.parse(app.dateTime);
      return isNaN(t) ? 0 : t;
    }
    if (app?.appointmentDate && app?.appointmentTime) {
      const dtStr = `${app.appointmentDate}T${app.appointmentTime}`;
      const t = Date.parse(dtStr);
      return isNaN(t) ? 0 : t;
    }
    return 0;
  };

  // Apply doctor filter first
  const baseDoctorGroups = filterDoctor && appointmentsByDoctor[filterDoctor]
    ? { [filterDoctor]: appointmentsByDoctor[filterDoctor] }
    : appointmentsByDoctor;

  // Build queue appointments based on doctor filter
  const queueAppointments = Object.values(baseDoctorGroups).flatMap(doctorGroup =>
    (doctorGroup?.appointments || []).filter(appointment => {
      // Filter by status - include Completed appointments
      const statusMatch = ['Scheduled', 'InQueue', 'InProgress', 'Completed'].includes(appointment.status);

      // Filter by date if filterDate is set
      let dateMatch = true;
      if (filterDate) {
        const appointmentDate = appointment.appointmentDate ||
          (appointment.dateTime ? format(new Date(appointment.dateTime), 'yyyy-MM-dd') : null);
        dateMatch = appointmentDate === filterDate;
      }

      return statusMatch && dateMatch;
    })
  );

  // Filter appointments based on active tab, patient search, and sort by time
  const getFilteredAppointments = () => {
    let list = queueAppointments;
    if (activeQueueTab === 'waiting') {
      list = list.filter(app => app.status === 'InQueue');
    } else if (activeQueueTab === 'in-progress') {
      list = list.filter(app => app.status === 'InProgress');
    } else if (activeQueueTab === 'completed') {
      list = list.filter(app => app.status === 'Completed');
    }

    // Apply patient search if provided
    const term = patientSearch?.trim().toLowerCase();
    if (term) {
      list = list.filter(app => {
        const p = app.patient || {};
        const name = `${p.firstName || ''} ${p.lastName || ''}`.toLowerCase();
        const phone = (p.mobile || p.phone || '').toLowerCase();
        const id = (app._id || '').toLowerCase();
        return name.includes(term) || phone.includes(term) || id.includes(term);
      });
    }

    // Sort by appointment time ascending
    return list.slice().sort((a, b) => getApptTimestamp(a) - getApptTimestamp(b));
  };

  const filteredAppointments = getFilteredAppointments();

  const resetVitalsForm = () => {
    setVitalsForm({ ...INITIAL_VITALS_FORM });
    setVitalsErrors({});
  };

  // Validate a single vital field
  const validateVitalField = (field, value) => {
    if (value === '' || value === null || value === undefined) {
      return null; // Empty values are allowed (optional fields)
    }

    const validation = VITALS_VALIDATION[field];
    if (!validation) return null;

    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return `${validation.label} must be a valid number`;
    }

    if (numValue < validation.min || numValue > validation.max) {
      return `${validation.label} must be between ${validation.min}-${validation.max}`;
    }

    return null;
  };

  const computeBmi = (weight, heightCm) => {
    if (!weight || !heightCm) {
      return '';
    }

    const weightNum = Number(weight);
    const heightNum = Number(heightCm);
    if (Number.isNaN(weightNum) || Number.isNaN(heightNum) || heightNum <= 0) {
      return '';
    }

    const bmiValue = weightNum / Math.pow(heightNum / 100, 2);
    if (!Number.isFinite(bmiValue)) {
      return '';
    }

    return bmiValue.toFixed(1);
  };

  const openVitalsModal = (appointment) => {
    if (!appointment) {
      return;
    }

    const existingVitals = appointment.vitals || {};
    const formValues = Object.keys(INITIAL_VITALS_FORM).reduce((acc, key) => {
      const value = existingVitals[key];
      acc[key] = value === null || value === undefined ? '' : String(value);
      return acc;
    }, {});

    if (!formValues.bmi) {
      formValues.bmi = computeBmi(formValues.weightKg, formValues.heightCm);
    }

    setVitalsForm(formValues);
    setVitalsModal({ open: true, appointment });
  };

  const closeVitalsModal = () => {
    setVitalsModal({ open: false, appointment: null });
    setVitalsSubmitting(false);
    resetVitalsForm();
  };

  const handleVitalsInputChange = (field, value) => {
    const sanitizedValue = value === '' ? '' : value;

    setVitalsForm((prev) => {
      const next = { ...prev, [field]: sanitizedValue };

      if (field === 'heightCm' || field === 'weightKg') {
        const updatedHeight = field === 'heightCm' ? sanitizedValue : next.heightCm;
        const updatedWeight = field === 'weightKg' ? sanitizedValue : next.weightKg;
        next.bmi = computeBmi(updatedWeight, updatedHeight);
      }

      return next;
    });

    // Validate the field on change
    const error = validateVitalField(field, sanitizedValue);
    setVitalsErrors(prev => {
      const updated = { ...prev };
      if (error) {
        updated[field] = error;
      } else {
        delete updated[field];
      }
      return updated;
    });
  };

  const handleVitalsSubmit = async (event) => {
    event.preventDefault();

    const appointmentId = vitalsModal.appointment?._id || vitalsModal.appointment?.id;
    if (!appointmentId) {
      showError('Unable to determine appointment. Please close and retry.');
      return;
    }

    // Validate all vitals before saving
    const errors = {};
    Object.keys(vitalsForm).forEach(field => {
      if (vitalsForm[field] !== '' && vitalsForm[field] !== null && vitalsForm[field] !== undefined) {
        const error = validateVitalField(field, vitalsForm[field]);
        if (error) {
          errors[field] = error;
        }
      }
    });

    if (Object.keys(errors).length > 0) {
      setVitalsErrors(errors);
      showError('Please fix validation errors before saving');
      return;
    }

    const vitalsPayload = Object.entries(vitalsForm).reduce((acc, [key, rawValue]) => {
      if (rawValue === '' || rawValue === null || rawValue === undefined) {
        return acc;
      }

      const numericValue = Number(rawValue);
      if (Number.isNaN(numericValue)) {
        return acc;
      }

      acc[key] = numericValue;
      return acc;
    }, {});

    if (!Object.keys(vitalsPayload).length) {
      showWarning('Enter at least one vital before saving.');
      return;
    }

    setVitalsSubmitting(true);

    try {
      await appointmentAPI.update(appointmentId, { vitals: vitalsPayload });
      const patientName = vitalsModal.appointment?.patient?.firstName
        ? `${vitalsModal.appointment.patient.firstName} ${vitalsModal.appointment.patient.lastName || ''}`.trim()
        : 'patient';
      showSuccess(`Vitals saved successfully for ${patientName}.`);

      if (typeof onRefreshAppointments === 'function') {
        await onRefreshAppointments();
      }

      closeVitalsModal();
    } catch (error) {
      console.error('Failed to save vitals:', error);
      const message = error.response?.data?.message || 'Unable to save vitals. Please try again.';
      showError(message);
    } finally {
      setVitalsSubmitting(false);
    }
  };

  const handleCheckIn = () => {
    // Handle check-in logic here
    console.log('Checking in patient:', selectedPatient);
    console.log('Notes:', checkInNotes);

    if (selectedPatient) {
      showSuccess(`Patient ${selectedPatient.patient?.firstName || 'Unknown'} checked in successfully`);
      setSelectedPatient(null);
      setCheckInNotes('');
    } else {
      showWarning('Please select a patient to check in');
    }
  };

  // Get status badge styles based on status
  const getStatusBadgeStyles = (status) => {
    switch (status) {
      case 'InProgress':
        return 'bg-theme-inprogress/20 text-theme-inprogress border border-theme-inprogress';
      case 'Completed':
        return 'bg-theme-success/20 text-green-700 border border-theme-success';
      case 'InQueue':
        return 'bg-theme-blue/30 text-theme-border border border-theme-border';
      case 'Cancelled':
        return 'bg-theme-error/20 text-red-700 border border-theme-error';
      default:
        return 'bg-gray-100 text-theme-heading border border-gray-300';
    }
  };

  // New appointment card for queue management - Redesigned
  const QueueAppointmentCard = ({ appointment }) => {
    const doctorInfo = Object.values(appointmentsByDoctor).find(doctorGroup =>
      doctorGroup.appointments.some(app => app._id === appointment._id)
    )?.doctor;

    // Get appointment date for display
    const appointmentDate = appointment.appointmentDate ||
      (appointment.dateTime ? formatISTDateShort(appointment.dateTime) : 'N/A');
    
    const appointmentTime = appointment.dateTime ? 
      format(new Date(appointment.dateTime), 'hh:mm a') : 'N/A';

    // Get status badge based on current status
    const getStatusBadge = () => {
      switch (appointment.status) {
        case 'InProgress':
          return (
            <span className="inline-flex items-center px-7 py-2 rounded-full text-md font-semibold bg-amber-100 text-amber-700 border border-amber-300">
              ● In Progress
            </span>
          );
        case 'Completed':
          return (
            <span className="inline-flex items-center px-7 py-2 rounded-full text-md font-semibold bg-green-100 text-green-700 border border-green-300">
              ● Completed
            </span>
          );
        case 'InQueue':
          return (
            <span className="inline-flex items-center px-7 py-2 rounded-full text-md font-semibold bg-[#9DC9D4]/30 text-[#2D869C] border border-[#2D869C]/50">
              ● In Queue
            </span>
          );
        case 'Cancelled':
          return (
            <span className="inline-flex items-center px-7 py-2 rounded-full text-md font-semibold bg-red-100 text-red-700 border border-red-300">
              ● Cancelled
            </span>
          );
        default:
          return (
            <span className="inline-flex items-center px-7 py-2 rounded-full text-md font-semibold bg-gray-100 text-gray-700 border border-gray-300">
              ● Scheduled
            </span>
          );
      }
    };

    return (
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-all">
        <div className="flex justify-between">
          {/* Left Side - Patient Info */}
          <div className="flex-1">
            {/* Doctor Name */}
            <h4 className="text-lg font-semibold text-gray-600 mb-2 uppercase tracking-wide">
              Dr. {doctorInfo?.name || 'Unknown'}
            </h4>

            {/* Patient Name with Phone */}
            <div className="flex items-center gap-4 mb-4">
              <span className="text-xl font-semibold text-black">
                {appointment.patient?.firstName ?
                  `${appointment.patient.firstName} ${appointment.patient.lastName || ''}`.trim() :
                  'Patient'
                }
              </span>
              <span className="flex items-center text-gray-600 text-lg font-medium">
                <Phone size={18} className="mr-2" />
                {appointment.patient?.mobile || appointment.patient?.phone || 'N/A'}
              </span>
            </div>

            {/* Info Badges - All with teal theme */}
            <div className="flex flex-wrap gap-3">
              <span className="inline-flex items-center px-4 py-2 rounded-lg border border-[#2D869C]/30 text-[#2D869C] text-lg font-medium bg-[#9DC9D4]/20">
                <Calendar size={16} className="mr-2" />
                {appointmentDate}
              </span>
              <span className="inline-flex items-center px-4 py-2 rounded-lg border border-[#2D869C]/30 text-[#2D869C] text-lg font-medium bg-[#9DC9D4]/20">
                <Clock size={16} className="mr-2" />
                {appointmentTime}
              </span>
              <span className="inline-flex items-center px-4 py-2 rounded-lg border border-[#2D869C]/30 text-[#2D869C] text-lg font-medium bg-[#9DC9D4]/20">
                <Timer size={16} className="mr-2" />
                {appointment.durationMin || appointment.duration || 30} mins
              </span>
              <span className="inline-flex items-center px-4 py-2 rounded-lg border border-[#2D869C]/30 text-[#2D869C] text-lg font-medium bg-[#9DC9D4]/20">
                <Users size={16} className="mr-2" />
                Queue: {appointment.queuePosition ?? 'N/A'}
              </span>
            </div>
          </div>

          {/* Right Side - Status Badge & Actions */}
          <div className="flex flex-col items-end ml-6">
            {/* Single Status Badge based on actual status */}
            <div className="mb-4">
              {getStatusBadge()}
            </div>

            {/* Action Buttons - Stacked Vertically */}
            <div className="flex flex-col gap-3">
              <button
                onClick={() => onJoinQueue(appointment._id)}
                disabled={loading || appointment.status !== 'Scheduled'}
                className="px-5 py-2.5 text-lg font-medium text-white bg-[#2D869C] hover:bg-[#2D869C]/90 rounded-xl transition-all shadow-sm hover:shadow-md disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
              >
                Add to queue
              </button>
              <button
                onClick={() => openVitalsModal(appointment)}
                disabled={appointment.status === 'Completed'}
                className="px-5 py-2.5 text-lg font-medium text-[#2D869C] border-2 border-[#2D869C] rounded-xl hover:bg-[#9DC9D4]/20 transition-all shadow-sm hover:shadow-md disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {appointment.vitals && Object.keys(appointment.vitals).length > 0 ? 'Update Vitals' : 'Add Vitals'}
              </button>
              <button
                onClick={() => onCancelAppointment(appointment._id)}
                disabled={loading || ['Completed', 'Cancelled'].includes(appointment.status)}
                className="px-5 py-2.5 text-lg font-medium text-red-600 border-2 border-red-500 rounded-xl hover:bg-red-50 transition-all shadow-sm hover:shadow-md disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const WaitingPatientCard = ({ patient }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-base font-medium text-gray-900">{patient.name}</h3>
            <StatusBadge status={patient.status} className="text-xs" />
          </div>

          <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mb-3">
            <div className="flex items-center">
              <Clock size={14} className="mr-2 text-gray-400" />
              {patient.appointmentTime}
            </div>
            <div className="flex items-center">
              <User size={14} className="mr-2 text-gray-400" />
              {patient.doctor}
            </div>
          </div>

          <div className="flex items-center text-sm">
            <span className="text-orange-600 bg-orange-50 px-2 py-1 rounded-full">
              Wait: {patient.waitTime} min
            </span>
          </div>
        </div>

        <div className="ml-4 flex flex-col space-y-2">
          {patient.status === 'Checked In' && (
            <button
              onClick={() => handleCallPatient(patient.id)}
              className={`${BUTTON_STYLES.primary} text-xs px-3 py-1.5`}
            >
              Call Patient
            </button>
          )}
          {patient.status === 'Called' && (
            <button
              className={`${BUTTON_STYLES.success} text-xs px-3 py-1.5`}
            >
              In Progress
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const vitalsPatientName = vitalsModal.appointment?.patient?.firstName
    ? `${vitalsModal.appointment.patient.firstName} ${vitalsModal.appointment.patient.lastName || ''}`.trim()
    : vitalsModal.appointment?.patient?.name || 'patient';

  return (
    <div className="flex-1 p-6" style={{ fontFamily: 'Poppins' }}>
      {vitalsModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-gray-900/40"
            onClick={vitalsSubmitting ? undefined : closeVitalsModal}
          ></div>
          <div className="relative z-10 w-full max-w-3xl rounded-xl bg-white shadow-xl">
            <div className="flex items-center justify-between bg-[#2D869C] px-6 py-5 rounded-t-xl">
              <div>
                <h3 className="text-2xl font-semibold text-white">{vitalsModal.appointment?.vitals ? 'Update Vitals' : 'Add Vitals'}</h3>
                <p className="text-lg font-medium text-white/90 mt-1">
                  Recording vitals for {vitalsPatientName}
                </p>
              </div>
              <button
                type="button"
                onClick={closeVitalsModal}
                disabled={vitalsSubmitting}
                className="text-white hover:text-white/80 transition-colors text-3xl font-bold leading-none"
                aria-label="Close vitals modal"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleVitalsSubmit} className="px-6 py-5 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {vitalFieldMeta.map(({ key, label, step, placeholder, readOnly }) => (
                  <div key={key}>
                    <label className="block text-lg font-medium text-gray-700 mb-2">{label}</label>
                    <input
                      type="number"
                      step={step}
                      value={vitalsForm[key]}
                      onChange={(e) => handleVitalsInputChange(key, e.target.value)}
                      placeholder={placeholder}
                      readOnly={readOnly}
                      className={`w-full rounded-xl border-2 ${vitalsErrors[key] ? 'border-red-500' : readOnly ? 'bg-gray-50 text-gray-600 border-gray-200 cursor-not-allowed' : 'border-gray-200'} px-4 py-3 text-lg font-medium focus:outline-none focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] hover:border-[#2D869C] transition-all shadow-sm`}
                    />
                    {vitalsErrors[key] && (
                      <p className="text-md text-red-500 mt-2 font-medium">{vitalsErrors[key]}</p>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeVitalsModal}
                  disabled={vitalsSubmitting}
                  className="px-6 py-3 text-lg font-medium text-gray-700 border-2 border-gray-300 rounded-xl hover:bg-gray-50 transition-all shadow-sm hover:shadow disabled:opacity-50"
                >
                  Cancel
                </button>
                <LoadingButton
                  loading={vitalsSubmitting}
                  type="submit"
                  className="px-6 py-3 text-lg font-medium text-white bg-[#2D869C] hover:bg-[#2D869C]/90 rounded-xl transition-all shadow-sm hover:shadow-md flex items-center justify-center disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Save Vitals
                </LoadingButton>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Header Section */}
      <div className="mb-6">
        <h2 className="text-3xl font-semibold text-black mb-2">Patient Check-In & Queue Management</h2>

        {/* Search Bar */}
        
      </div>

      {/* Queue Management */}
      <div className="mb-6">
        
        {/* Tabs and Filters Row */}
        <div className="flex items-center justify-between mb-6">
          {/* Queue Tabs */}
          <div className="flex items-center bg-gray-100 rounded-full p-1 gap-1">
            {[
              { id: 'all', label: 'All', count: queueAppointments.length },
              { id: 'waiting', label: 'In Queue', count: queueAppointments.filter(app => app.status === 'InQueue').length },
              { id: 'in-progress', label: 'In Progress', count: queueAppointments.filter(app => app.status === 'InProgress').length },
              { id: 'completed', label: 'Completed', count: queueAppointments.filter(app => app.status === 'Completed').length }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveQueueTab(tab.id)}
                className={`px-5 py-2.5 text-lg font-medium rounded-full transition-all duration-200 whitespace-nowrap ${activeQueueTab === tab.id
                  ? 'bg-[#2D869C] text-white shadow-sm'
                  : 'text-gray-600 hover:text-[#2D869C] hover:bg-gray-200'
                  }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          

          {/* Filters on Right */}
          <div className="flex items-center gap-3">
            <div className="relative max-w-md">
          <Search size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={patientSearch}
            onChange={(e) => setPatientSearch(e.target.value)}
            placeholder="Search Doctor, Patient"
            className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium bg-white hover:border-[#2D869C] transition-all shadow-sm"
          />
        </div>
            {/* Date Filter */}
            <div className="relative">
              <Calendar size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#2D869C] pointer-events-none" />
              <input
                type="date"
                value={filterDate}
                onChange={(e) => handleDateFilterChange(e.target.value)}
                className="pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl bg-white text-lg font-medium focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                title="Filter by date"
              />
            </div>
            {/* Doctor Filter */}
            <div className="relative">
              <select
                value={filterDoctor}
                onChange={(e) => setFilterDoctor(e.target.value)}
                className="pl-4 pr-10 py-3 border-2 border-gray-200 rounded-xl bg-white text-lg font-medium focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-gray-700 appearance-none cursor-pointer hover:border-[#2D869C] transition-all shadow-sm"
                title="Filter by doctor"
              >
                <option value="" className="text-gray-700">All doctors</option>
                {Object.entries(appointmentsByDoctor).map(([doctorId, doctorGroup]) => (
                  <option key={doctorId} value={doctorId} className="text-gray-700">
                    Dr. {doctorGroup.doctor?.name || 'Unknown'}
                  </option>
                ))}
              </select>
              <ChevronDown size={20} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#2D869C] pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Appointments Grid */}
        <div className="">
          {filteredAppointments.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {filteredAppointments.map((appointment) => (
                <QueueAppointmentCard key={appointment._id} appointment={appointment} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
              <div className="flex justify-center mb-4">
                <div className="w-20 h-20 rounded-full bg-[#9DC9D4]/30 flex items-center justify-center border-2 border-[#2D869C]/30">
                  <Users size={48} className="text-[#2D869C]" />
                </div>
              </div>
              <h3 className="text-2xl font-semibold text-gray-600 mb-2">
                {activeQueueTab === 'all' ? 'No appointments' :
                  activeQueueTab === 'waiting' ? 'No patients in queue' :
                    activeQueueTab === 'in-progress' ? 'No appointments in progress' :
                      'No completed appointments'}
              </h3>
              <p className="text-lg text-gray-500 font-medium">
                {activeQueueTab === 'all' ? 'There are no appointments scheduled for today' :
                  activeQueueTab === 'waiting' ? 'All appointments are either scheduled or in progress' :
                    activeQueueTab === 'in-progress' ? 'No appointments are currently being handled' :
                      'No appointments have been completed yet'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckInOut;
